import { Tab, TabList, TabPanel, Tabs } from "@react-spectrum/s2";
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Key } from 'react';
import { AppScreenTitle } from './AppScreenTitle';

import './DomainPageShell.css';

export interface NavItem {
  id: string;
  label: string;
  /** Hide this item from the rendered list (useful for permission gating). */
  hidden?: boolean;
}

export interface DomainPageShellProps {
  title: React.ReactNode;
  /** Bold prefix before the title (e.g. domain name): renders "Context ▏ Title". */
  context?: React.ReactNode;
  trailing?: React.ReactNode;
  /**
   * Description rendered **below** the divider, not inside the header band.
   * Use this for the "Add content" pattern; use AppScreenTitle's `subtitle`
   * only when text belongs inside the header strip.
   */
  intro?: React.ReactNode;
  /** Horizontal tab bar below the header. Mutually exclusive with `sidebar`. */
  tabs?: NavItem[];
  /** Vertical sidebar to the left of the content. Mutually exclusive with `tabs`. */
  sidebar?: NavItem[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  /** Omit the page title band (e.g. a file view that fills the whole page). */
  hideHeader?: boolean;
}

/**
 * Shared page shell for every domain-level and org-level view.
 *
 * Renders:
 *   1. AppScreenTitle ("Context ▏ Title" + optional trailing)
 *   2. Optional horizontal tabs OR vertical sidebar
 *   3. Scrollable content area with optional intro paragraph
 */
export const DomainPageShell: React.FC<DomainPageShellProps> = ({
  title,
  context,
  trailing,
  intro,
  tabs,
  sidebar,
  activeTab,
  onTabChange,
  children,
  className = '',
  bodyClassName = '',
  headerClassName = '',
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  const visibleTabs = tabs?.filter((tab) => !tab.hidden);
  const visibleSidebar = sidebar?.filter((s) => !s.hidden);
  const selectedTab = activeTab ?? visibleTabs?.[0]?.id;

  const contentBody = (
    <div className={`dps-content-area ${bodyClassName}`.trim()}>
      {intro != null && <div className="dps-intro">{intro}</div>}
      {children}
    </div>
  );

  return (
    <div className={`dps-root ${className}`.trim()}>
      {!hideHeader && (
        <AppScreenTitle
          className={`dps-header ${headerClassName}`.trim()}
          title={title}
          context={context}
          trailing={trailing}
        />
      )}

      {visibleTabs && visibleTabs.length > 0 ? (
        <Tabs
          aria-label={t('components.domainPageShell.tabsAria', {
            title: typeof title === 'string' ? title : t('components.domainPageShell.pageFallback'),
          })}
          selectedKey={selectedTab}
          onSelectionChange={(key: Key) => onTabChange?.(String(key))}
          UNSAFE_style={{ flex: 1, minHeight: 0 }}>
          <TabList
          UNSAFE_className="fb-tabs dps-tab-list">
            {visibleTabs.map((tab) => (
              <Tab id={tab.id} key={tab.id}>{tab.label}</Tab>
            ))}
          </TabList>
          {visibleTabs.map((tab) => (
            <TabPanel key={tab.id} id={tab.id}>
              {tab.id === selectedTab ? contentBody : null}
            </TabPanel>
          ))}
        </Tabs>
      ) : visibleSidebar && visibleSidebar.length > 0 ? (
        <div className="dps-sidebar-layout">
          <div className="dps-sidebar">
            {visibleSidebar.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`dps-sidebar-item ${activeTab === item.id ? 'dps-sidebar-item--active' : ''}`}
                onClick={() => onTabChange?.(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {contentBody}
        </div>
      ) : (
        contentBody
      )}
    </div>
  );
};
